import { useState, useEffect } from "react"
import { useNavigate } from "@tanstack/react-router"
import { Spin, Result, Layout, Button } from "@mimber/ui-web"
import { useOrganizations } from "@mimber/client/mimber-organization"
import { CreateOrganizationModal } from "@/components/modals"
import type { OrganizationGateProps } from "./OrganizationGate.types"
import { AuthorizationGate } from "../AuthorizationGate"
import { Action, Resource } from "@mimber/schema"
import {
  getSelectedOrganizationId,
  setSelectedOrganizationId,
} from "@/utils/organization"
import { AppHeader } from "../../blocks"

const { Content, Header } = Layout

export const OrganizationGate: React.FunctionComponent<
  OrganizationGateProps
> = ({ children }) => {
  const navigate = useNavigate()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { data, isLoading, isError, error } = useOrganizations()

  // Set the first organization as selected if none is currently selected
  useEffect(() => {
    if (data && data.data.length > 0) {
      const selectedId = getSelectedOrganizationId()
      const hasValidSelectedOrganization = selectedId
        ? data.data.some((organization) => organization.uuid === selectedId)
        : false

      if (!hasValidSelectedOrganization) {
        // Set first organization as default when current selection is missing or invalid
        setSelectedOrganizationId(data.data[0].uuid)
      }
    }
  }, [data])

  // Show loading while fetching organizations
  if (isLoading) {
    return (
      <Layout style={{ minHeight: "100vh" }}>
        <Content
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "var(--color-background)",
          }}
        >
          <div className="flex flex-col items-center text-center">
            <Spin size="large" />
            <p className="mt-4 text-muted-foreground">
              Loading organizations...
            </p>
          </div>
        </Content>
      </Layout>
    )
  }

  // Show error if fetching organizations failed
  if (isError && error) {
    return (
      <Layout style={{ minHeight: "100vh" }}>
        <Content
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "2rem",
            background: "var(--color-background)",
          }}
        >
          <div className="max-w-md w-full">
            <Result
              status="error"
              title="Failed to Load Organizations"
              subTitle={
                error?.message ||
                "An error occurred while loading organizations"
              }
            />
          </div>
        </Content>
      </Layout>
    )
  }

  // Show message if user has no organizations
  if (data && data.data.length === 0) {
    return (
      <>
        <Layout style={{ minHeight: "100vh" }}>
          <Header
            style={{
              padding: 0,
              display: "flex",
              alignItems: "center",
            }}
          >
            <AppHeader />
          </Header>
          <Content
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "2rem",
              background: "var(--color-background)",
            }}
          >
            <div className="max-w-md w-full">
              <Result
                status="info"
                title="No Organizations Found"
                subTitle="You are not a member of any organizations yet. Create your first organization to get started."
                extra={[
                  <Button
                    key="create"
                    type="primary"
                    size="large"
                    onClick={() => navigate({ to: "/organizations/create" })}
                  >
                    Create Organization
                  </Button>,
                  <Button
                    key="quick-create"
                    type="link"
                    onClick={() => setIsModalOpen(true)}
                  >
                    Quick-create organization
                  </Button>,
                ]}
              />
            </div>
          </Content>
        </Layout>
        <CreateOrganizationModal
          open={isModalOpen}
          onCancel={() => setIsModalOpen(false)}
          onSuccess={() => {
            setIsModalOpen(false)
            // Organizations list will auto-refresh due to query invalidation
          }}
        />
      </>
    )
  }

  // User has at least one organization - render children
  return (
    <AuthorizationGate
      resource={Resource.ORGANIZATION}
      action={Action.READ}
      fallback={
        <Layout style={{ minHeight: "100vh" }}>
          <Content
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "2rem",
              background: "var(--color-background)",
            }}
          >
            <div className="max-w-md w-full">
              <Result
                status="error"
                title="Access Denied"
                subTitle="You do not have permission to access this organization."
              />
            </div>
          </Content>
        </Layout>
      }
    >
      {children}
    </AuthorizationGate>
  )
}
